import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, useMotionValue, useAnimation } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface Pack {
  id: number;
  name: string;
  description: string;
  price: number;
  price_display: string;
  features: string[];
  is_featured: boolean;
  is_active: boolean;
  order: number;
}

const PacksSection: React.FC = () => {
  const [packs, setPacks] = useState<Pack[]>([]);
  const [loading, setLoading] = useState(true);
  const [duplicatedPacks, setDuplicatedPacks] = useState<Pack[]>([]);
  const [isPaused, setIsPaused] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const navigate = useNavigate();
  const x = useMotionValue(0);
  const controls = useAnimation();
  const containerRef = useRef<HTMLDivElement>(null);

  const loadPacks = async () => {
    try {
      const response = await api.get('/api/packs/');
      const data = response.data;
      const activePacks = (data.packs || data || [])
        .filter((pack: Pack) => pack.is_active)
        .sort((a: Pack, b: Pack) => a.order - b.order);
      setPacks(activePacks);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const startInfiniteScroll = useCallback(async () => {
    if (isPaused || !isMounted || packs.length === 0) return;
    
    const cardWidth = 400;
    const totalWidth = cardWidth * packs.length;
    
    await controls.start({
      x: -totalWidth,
      transition: {
        duration: packs.length * 5,
        ease: "linear",
        repeat: Infinity,
      }
    });
  }, [isPaused, isMounted, packs.length, controls]);

  const pauseScroll = () => {
    setIsPaused(true);
    controls.stop();
  };

  const resumeScroll = useCallback(() => {
    if (!isMounted) return;
    setIsPaused(false);
  }, [isMounted]);

  useEffect(() => {
    setIsMounted(true);
    loadPacks();
  }, []);

  useEffect(() => {
    if (packs.length > 0 && isMounted) {
      const duplicated = [...packs, ...packs, ...packs];
      setDuplicatedPacks(duplicated);
    }
  }, [packs, isMounted]);

  useEffect(() => {
    if (packs.length > 0 && isMounted && !isPaused) {
      startInfiniteScroll();
    }
  }, [packs.length, isMounted, isPaused, startInfiniteScroll]);

  const handlePrevious = () => {
    if (!isMounted) return;
    const currentX = x.get();
    controls.start({
      x: currentX + 400,
      transition: { duration: 0.5, ease: [0.19, 1, 0.22, 1] }
    });
  };

  const handleNext = () => {
    if (!isMounted) return;
    const currentX = x.get();
    controls.start({
      x: currentX - 400,
      transition: { duration: 0.5, ease: [0.19, 1, 0.22, 1] }
    });
  };

  if (loading) return (
    <section className="relative py-24 bg-gradient-to-br from-gray-50 via-white to-amber-50/30 overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center">
          <div className="inline-block animate-pulse text-amber-400">Chargement des packs...</div>
        </div>
      </div>
    </section>
  );
  
  if (packs.length === 0) return null;

  return (
    <section className="relative py-24 bg-gradient-to-br from-gray-50 via-white to-amber-50/30 overflow-hidden">
      {/* Particules dorées */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-amber-400/30 rounded-full"
            animate={{
              y: [Math.random() * 100 + 100, -100],
              x: [Math.random() * 100, Math.random() * 100 + 50],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: Math.random() * 8 + 12,
              repeat: Infinity,
              ease: "linear",
            }}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
          />
        ))}
      </div>

      {/* Gradient gold overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-400/5 via-transparent to-purple-500/5 pointer-events-none" />
      
      {/* Mesh gradient pattern */}
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(251, 191, 36, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(168, 85, 247, 0.1) 0%, transparent 50%)'
      }} />

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 3, repeat: Infinity }}
            className="inline-block mb-4"
          >
            <span className="text-amber-400 font-light text-sm uppercase tracking-[0.4em]">
              ✦ Accompagnement ✦
            </span>
          </motion.div>
          <h2 className="text-5xl md:text-7xl font-thin text-gray-900 mb-4 tracking-tight">
            Nos Packs d'Accompagnement
          </h2>
          <div className="w-24 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent mx-auto" />
        </div>

        {/* Carrousel avec plusieurs cards */}
        <div 
          className="relative max-w-7xl mx-auto overflow-hidden" 
          ref={containerRef}
          onMouseEnter={pauseScroll}
          onMouseLeave={resumeScroll}
        >
          <motion.div
            className="flex gap-6 cursor-grab active:cursor-grabbing"
            style={{ x }}
            animate={controls}
            drag="x"
            dragConstraints={{ left: -400 * packs.length, right: 0 }}
            dragElastic={0.2}
            dragTransition={{ bounceStiffness: 200, bounceDamping: 20 }}
            onDragStart={pauseScroll}
            onDragEnd={(_, info) => {
              const swipeThreshold = 50;
              if (Math.abs(info.offset.x) > swipeThreshold) {
                if (info.offset.x > 0) handlePrevious();
                else handleNext();
              }
              resumeScroll();
            }}
          >
            {duplicatedPacks.map((pack, index) => (
              <motion.div
                key={`${pack.id}-${index}`}
                initial={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.03, y: -10 }}
                className="flex-shrink-0 w-[350px] cursor-pointer"
                onClick={() => navigate('/packs')}
              >
                <div className="relative group">
                  {/* Glass card with premium gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white via-amber-50/50 to-white rounded-2xl opacity-90 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative bg-white/80 backdrop-blur-xl border border-amber-400/30 rounded-2xl p-6 shadow-xl hover:shadow-2xl hover:shadow-amber-500/20 transition-all duration-500 hover:border-amber-500/50">
                  {/* Badge featured */}
                  {pack.is_featured && (
                    <div className="absolute -top-3 -right-3 bg-gradient-to-br from-amber-400 to-amber-600 text-black px-4 py-1 rounded-full text-xs font-bold uppercase shadow-lg animate-pulse">
                      ★ Premium
                    </div>
                  )}

                  {/* Nom en gold et bold */}
                  <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500 mb-3 tracking-tight">
                    {pack.name}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-700 font-light mb-6 text-sm leading-relaxed min-h-[3rem]">
                    {pack.description}
                  </p>

                  {/* Prix */}
                  <div className="mb-4">
                    <div className="text-3xl font-thin text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500">
                      {pack.price_display}
                    </div>
                  </div>

                  {/* Features */}
                  {pack.features && pack.features.length > 0 && (
                    <ul className="space-y-2 mb-6">
                      {pack.features.slice(0, 4).map((feature, idx) => (
                        <li key={idx} className="flex items-start text-xs text-gray-600 font-light">
                          <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-1.5 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                      {pack.price >= 50000 && (
                        <li className="flex items-start text-xs text-gray-600 font-light">
                          <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-1.5 mr-2 flex-shrink-0" />
                          Paiement échelonné sur plusieurs mois
                        </li>
                      )}
                    </ul>
                  )}

                  {/* Bouton */}
                  <motion.div
                    whileHover={{ x: 5 }}
                    className="flex items-center text-amber-600 text-sm font-medium"
                  >
                    <span>Découvrir</span>
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </motion.div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Boutons navigation */}
          <motion.button
            whileHover={{ scale: 1.1, x: -5 }}
            whileTap={{ scale: 0.9 }}
            onClick={handlePrevious}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white/95 backdrop-blur-md border border-amber-400/50 rounded-full flex items-center justify-center text-amber-600 hover:bg-gradient-to-br hover:from-amber-500 hover:to-amber-600 hover:text-white hover:border-amber-600 transition-all duration-300 z-10 shadow-lg hover:shadow-xl hover:shadow-amber-500/30"
          >
            <ChevronLeft className="w-5 h-5" />
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.1, x: 5 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-white/95 backdrop-blur-md border border-amber-400/50 rounded-full flex items-center justify-center text-amber-600 hover:bg-gradient-to-br hover:from-amber-500 hover:to-amber-600 hover:text-white hover:border-amber-600 transition-all duration-300 z-10 shadow-lg hover:shadow-xl hover:shadow-amber-500/30"
          >
            <ChevronRight className="w-5 h-5" />
          </motion.button>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <button
            onClick={() => navigate('/packs')}
            className="text-amber-400 hover:text-amber-300 font-bold text-lg tracking-wider transition-colors"
          >
            Voir tous nos packs →
          </button>
        </div>
      </div>
    </section>
  );
};

export default PacksSection;
